// React Related Imports
import { all, delay, put, takeLatest } from "redux-saga/effects";

// Api
import {
  changeUserPassword,
  resetUserPassword,
  userLogin,
  forgotPasswordFn,
} from "../../apis/AuthAPI";
import { getOrgProfileInfo } from "../../apis/OrgProfileAPI";

// Utils and Constants
import { Auth } from "../../constants/action-constants/AuthActionConstants";
import { OrgProfile } from "../../constants/action-constants/org/OrgProfileActionConstants";
import { LocalStorageKeys, UserRoles } from "../../constants/GeneralConstants";
import { setToken } from "../../utils/token";
import { setItem } from "../../utils/localstorage";
import { checkIsOnlyMonitoringJobUser } from "../../common-utils/user-utils/userUtils";

// functions
function* setItemsForOrgSubscription(orgProfile) {
  const {
    isOnlyApiSubscriptionActive = false,
    isPlannerAndApiSubscriptionActive = false,
  } = orgProfile || {};

  // setItems for key in local storage.
  setItem(
    LocalStorageKeys.IS_ORG_ONLY_API_SUBSCRIPTION_ACTIVE,
    isOnlyApiSubscriptionActive
  );
  setItem(
    LocalStorageKeys.IS_ORG_PLANNER_AND_API_SUBSCRIPTION_ACTIVE,
    isPlannerAndApiSubscriptionActive
  );

  // update redux state
  yield put({
    type: OrgProfile.GET_ORG_PROFILE_SUCCESS,
    payload: { orgProfile },
  });
}

// setItems For Platform Update Modal
function setItemsForPlatformUpdateModal(roles = [], orgProfile) {
  const { isOnlyApiSubscriptionActive = false } = orgProfile || {};

  // NOTE : this modal not required for orgs which have only API subscription active
  if (isOnlyApiSubscriptionActive) {
    return;
  }

  // NOTE :: this modal not required for seller users
  const isSellerUser = roles.includes(UserRoles.ROLE_SELLER);
  if (isSellerUser) {
    return;
  }

  // Setting key for first time user visit for "Platform-Updates-Form"
  setItem(LocalStorageKeys.PLATFORM_UPDATES_MODAL, "true");
}

export function* login(action) {
  try {
    //getting email and password from action
    const { email, password } = action.payload;

    const loggedInData = yield userLogin(email, password);

    // Check if the user belongs to the organizations white Listed..
    const { org: orgDetails = {}, roles = [], token } = loggedInData;
    const { id: orgId } = orgDetails;

    // If WhiteList Organizations are not defined we will not allow anyone
    // NOTE: We are making this mandatory to define the whitelist organization details
    const allowedOrgIds = process.env.REACT_APP_WHITELIST_ORG_IDS ?? "";
    if (!allowedOrgIds.includes(orgId)) {
      //If user does not fall into any of the whitelisted organizations
      // does not allow to login into the system..
      yield put({
        type: Auth.USER_LOGIN_FAILURE,
        payload: "You are not registered to login into the system.",
      });
      return;
    }

    // Condition to check for users which only have "Monitoring Job" (app) access
    // not allowing monitoring job only users to login into the system
    const isOnlyMonitoringJobUser = checkIsOnlyMonitoringJobUser(roles);
    if (isOnlyMonitoringJobUser) {
      yield put({
        type: Auth.USER_LOGIN_FAILURE,
        payload: "You don't have permission to login into the system.",
      });
      return;
    }

    //setting token in localstorage :: TODO remove the setToken.
    setToken(token);

    //store permissions in local storage
    setItem(LocalStorageKeys.PERMISSIONS, roles);

    //set user details in local storage
    setItem(LocalStorageKeys.USER, JSON.stringify(loggedInData));

    //set org type in local storage
    setItem(LocalStorageKeys.ORG_TYPE, orgDetails.type);

    // setItems for Org Subscriptions
    const orgProfile = yield getOrgProfileInfo();
    yield setItemsForOrgSubscription(orgProfile);

    // setItem for platform updates modal for first time user visit
    setItemsForPlatformUpdateModal(roles, orgProfile);

    //dispatching action
    yield put({
      type: Auth.USER_LOGIN_SUCCESS,
      payload: { loggedInData },
    });
  } catch (err) {
    yield put({
      type: Auth.USER_LOGIN_FAILURE,
      payload: err,
    });
  }
}

/**
 * Logout
 */
// TODO: This has no functional implementation, needs to be addressed...
export function* logout() {
  try {
    yield delay(200);

    yield put({
      type: Auth.USER_LOGOUT_SUCCESS,
    });
  } catch (err) {
    /* istanbul ignore next */
    yield put({
      type: Auth.USER_LOGOUT_FAILURE,
      payload: err,
    });
  }
}

/**
 * RESET PASSWORD
 */
export function* resetPassword(action) {
  try {
    const { newPassword, token } = action.payload;

    yield resetUserPassword(newPassword, token);
    yield put({
      type: Auth.RESET_PASSWORD_SUCCESS,
    });
  } catch (err) {
    yield put({
      type: Auth.RESET_PASSWORD_FAILURE,
      payload: err,
    });
  }
}

/**
 * Forgot Password
 */

function* forgotPassword(action) {
  try {
    const { email } = action.payload;
    yield forgotPasswordFn(email);
    yield put({
      type: Auth.FORGOT_PASSWORD_SUCCESS,
    });
  } catch (err) {
    yield put({
      type: Auth.FORGOT_PASSWORD_FAILURE,
      payload: err,
    });
  }
}

/**
 * CHANGE PASSWORD
 */
export function* changePassword(action) {
  try {
    //getting email from action
    const { oldPassword, newPassword } = action.payload.data;

    /**
     * TODO: this is not working right now,
     * NEEDS TO BE FIXED
     */
    yield changeUserPassword(oldPassword, newPassword);

    //dispatching action
    yield put({
      type: Auth.CHANGE_PASSWORD_SUCCESS,
    });
  } catch (err) {
    /* istanbul ignore next */
    yield put({
      type: Auth.CHANGE_PASSWORD_FAILURE,
      payload: err,
    });
  }
}

/**
 * User Sagas
 */
export default function* root() {
  yield all([
    takeLatest(Auth.USER_LOGIN, login),
    takeLatest(Auth.USER_LOGOUT, logout),
    takeLatest(Auth.RESET_PASSWORD, resetPassword),
    takeLatest(Auth.FORGOT_PASSWORD, forgotPassword),
    takeLatest(Auth.CHANGE_PASSWORD, changePassword),
  ]);
}
